import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { EducationField } from '../../../models/educationField';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent, ColumnConfig } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { LutService } from '../../../services/lut.service';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-education-field-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './education-field-list.component.html',
  styleUrl: './education-field-list.component.scss'
})
export class EducationFieldListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  educationFields = signal<EducationField[]>([]);
  isSearchVisible: boolean = false;
  isExporting: boolean = false;
  matchedItems: EducationField[] | undefined = undefined;
  rightItem: EducationField | undefined = undefined;

  // Pagination state
  currentPage: number = 1;
  pageSize: number = 25;
  totalCount: number = 0;
  sortBy: string = 'name';
  ascending: boolean = true;

  // Search filter state
  searchFilter: LookupTableSearchFilter = {};

  educationFieldsColumns: ColumnConfig[] = [
    { key: 'name', header: 'Name', type: 'text', sortable: true, editable: true }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private spinnerService: NgxSpinnerService,
    private lutService: LutService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadEducationFields();
  }

  loadEducationFields(): void {
    this.spinnerService.show();

    // Check if any search filters are active
    const hasSearchFilters = this.searchFilter.name || this.searchFilter.exactMatch;

    if (hasSearchFilters) {
      // Use search endpoint when filters are active
      const searchRequest = {
        pageNumber: this.currentPage,
        pageSize: this.pageSize,
        sortBy: this.sortBy,
        ascending: this.ascending,
        ...this.searchFilter
      };

      this.lutService.searchEducationFields(searchRequest).subscribe({
        next: (response) => {
          this.educationFields.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading education fields:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Education Fields:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    } else {
      // Use regular paginated endpoint when no filters are active
      this.lutService.getEducationFieldsPaginated(
        this.currentPage,
        this.pageSize,
        this.sortBy,
        this.ascending
      ).subscribe({
        next: (response) => {
          this.educationFields.set(response.data);
          this.totalCount = response.totalCount;
          this.spinnerService.hide();
        },
        error: (error) => {
          console.error('Error loading education fields:', error);
          this.spinnerService.hide();
          this.toast.error(`<strong>Failed to load Education Fields:</strong></br></br> ${Helpers.parseApiError(error)}`);
        }
      });
    }
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex + 1; // MatPaginator uses 0-based index
    this.pageSize = event.pageSize;
    this.loadEducationFields();
  }

  onSortChange(sort: Sort): void {
    // If sort direction is cleared (empty string), reset to default sort
    if (!sort.direction) {
      this.sortBy = 'name';
      this.ascending = true;
    } else {
      this.sortBy = sort.active;
      this.ascending = sort.direction === 'asc';
    }

    this.loadEducationFields();
  }

  onCreateEducationField(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  onExportToCsv(): void {
    if (this.isExporting) return;
    this.isExporting = true;
    this.spinnerService.show();

    this.lutService.exportEducationFieldsToCsv({
      name: this.searchFilter.name,
      exactMatch: this.searchFilter.exactMatch
    }).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `education_fields_export_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
        this.spinnerService.hide();
        this.isExporting = false;
        this.toast.success('Education fields exported successfully');
      },
      error: (error) => {
        console.error('Error exporting education fields:', error);
        this.toast.error(`<strong>Failed to export Education Fields:</strong></br></br> ${Helpers.parseApiError(error)}`);
        this.spinnerService.hide();
        this.isExporting = false;
      }
    });
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.searchFilter).filter(value => {
      if (typeof value === 'boolean') {
        return value === true;
      }
      return value !== undefined && value !== null && value !== '';
    }).length;
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.currentPage = 1; // Reset to first page when searching

    // Reset the paginator to first page visually
    if (this.lookupTable?.['paginator']) {
      this.lookupTable['paginator'].pageIndex = 0;
    }

    this.loadEducationFields();
  }

  onItemSelected(educationField: EducationField): void {
  }

  onReadyToMatch(event: {matchedItems?: EducationField[], rightItem?: EducationField}): void {
    this.matchedItems = event.matchedItems;
    this.rightItem = event.rightItem;
  }

  onEditEducationField(educationField: EducationField): void {
  }

  onDeleteEducationField(educationField: EducationField): void {
    this.confirmationDialogService.openDeleteConfirmationDialog(educationField.name)
      .subscribe(result => {
        if (result) {
          const originalEducationFields = this.educationFields();

          this.educationFields.update(educationFields => educationFields.filter(ef => ef.id !== educationField.id));

          this.spinnerService.show();
          this.lutService.deleteEducationField(educationField.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Education field deleted successfully');
              // Reload current page
              this.loadEducationFields();
            },
            error: (error) => {
              console.error('Error deleting education field:', error);
              this.spinnerService.hide();
              this.educationFields.set(originalEducationFields);
              this.toast.error(`<strong>Failed to delete Education Field:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: EducationField, newName: string} | {item: EducationField, newValue: any, columnKey: string}): void {
    const originalItem = { ...event.item };

    let updatedItem: EducationField;
    if ('newName' in event) {
      updatedItem = { ...event.item, name: event.newName };
    } else {
      updatedItem = { ...event.item, [event.columnKey]: event.newValue };
    }

    this.educationFields.update(educationFields =>
      educationFields.map(ef => ef.id === event.item.id ? updatedItem : ef)
    );

    this.spinnerService.show();
    this.lutService.updateEducationField(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Education field updated successfully');
        this.educationFields.update(educationFields =>
          educationFields.map(ef => ef.id === event.item.id ? response : ef)
        );
      },
      error: (error) => {
        console.error('Error updating education field:', error);
        this.spinnerService.hide();
        this.educationFields.update(educationFields =>
          educationFields.map(ef => ef.id === event.item.id ? originalItem : ef)
        );
        this.toast.error(`<strong>Failed to update Education Field:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    const tempId = crypto.randomUUID();
    const newEducationField: EducationField = {
      id: tempId,
      name: event['name'],
      systemReserved: false
    };

    this.educationFields.update(educationFields => [newEducationField, ...educationFields]);

    this.spinnerService.show();
    this.lutService.createEducationField({ name: event['name'], systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Education field created successfully');
        this.educationFields.update(educationFields =>
          educationFields.map(ef => ef.id === tempId ? response : ef)
        );
      },
      error: (error) => {
        console.error('Error creating education field:', error);
        this.spinnerService.hide();
        this.educationFields.update(educationFields =>
          educationFields.filter(ef => ef.id !== tempId)
        );
        this.toast.error(`<strong>Failed to create Education Field:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onEducationFieldCorrected(): void {
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    const itemsToMerge = this.matchedItems.map(item => item.name).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.name}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        const originalEducationFields = this.educationFields();

        this.educationFields.update(educationFields =>
          educationFields.filter(ef => !bulkMergeRequest.leftIds.includes(ef.id))
        );

        this.rightItem = undefined;
        this.matchedItems = undefined;

        this.spinnerService.show();
        this.lutService.mergeEducationFieldsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Education fields merged successfully');
            this.lookupTable.clearMatchingState();
            this.loadEducationFields();
          },
          error: (error) => {
            console.error('Error merging education fields:', error);
            this.spinnerService.hide();
            this.educationFields.set(originalEducationFields);
            this.rightItem = originalEducationFields.find(ef => ef.id === bulkMergeRequest.rightId);
            this.matchedItems = originalEducationFields.filter(ef => bulkMergeRequest.leftIds.includes(ef.id));
            this.toast.error(`<strong>Failed to merge Education Fields:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }
}
